'use client';

import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import Backdrop from '@mui/material/Backdrop';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import { InverseButton } from '@parkchirp/ui-library';
import React, { useState } from 'react';

import { paramToSlug } from '../../utils';

interface EditorModalProps {
	headerText: string;
	hasValidationErrors?: boolean;
	hasUnsavedChanges?: boolean;
	loading: boolean;
	headerBorder?: boolean;
	handleClose: () => void;
	handleSave: () => void | null;
	theme: any;
	children?: React.JSX.Element[] | React.JSX.Element;
	containerClassName?: string;
	saveDisabled?: boolean;
	modalWidth?: 'md' | 'max' | string;
	readOnly?: boolean;
}

export const EditorModal = ({
	headerText,
	hasValidationErrors = false,
	hasUnsavedChanges = false,
	loading,
	headerBorder = false,
	handleClose,
	handleSave,
	theme,
	children,
	containerClassName,
	saveDisabled = false,
	modalWidth = 'md',
	readOnly = false,
}: EditorModalProps) => {
	const [displayAlert, setDisplayAlert] = useState<boolean>(false);
	const [clicked, setClicked] = useState<boolean>(false);

	const protectClose = () => {
		if (hasUnsavedChanges && !displayAlert) {
			setDisplayAlert(true);
		} else {
			setClicked(false);
			handleClose();
		}
	};

	const onSaveClick = () => {
		setClicked(true);
		setDisplayAlert(false);
		handleSave();
	};

	let width: string;
	switch (modalWidth) {
		case 'max':
			width = '1500px';
			break;
		case 'md':
			width = '1100px';
			break;
		case 'sm':
			width = '400px';
			break;
		default:
			width = modalWidth;
	}

	return (
		<div
			className={`editor-modal-content ${containerClassName || ''}`}
			style={{ width }}
			data-cy={`${paramToSlug(headerText)}-modal-container`}
		>
			<Backdrop open={loading}>
				<CircularProgress color='inherit' data-cy='facility-modal-loading-indicator' />
			</Backdrop>
			<div>
				<div className={`editor-modal-header ${headerBorder && 'editor-modal-header-border'}`}>
					<h3 className='editor-modal-header-text'>{headerText}</h3>
					{!displayAlert && (
						<IconButton size='medium' onClick={protectClose} disabled={loading}>
							<CloseIcon fontSize='medium' data-cy='modal-close-button' />
						</IconButton>
					)}
				</div>
				{displayAlert && (
					<Alert className='editor-modal-alert' severity='error'>
						You have unsaved changes, click SAVE to keep your current changes, or DISCARD to leave.
					</Alert>
				)}
				{hasValidationErrors && !displayAlert && (
					<Alert className='editor-modal-alert' severity='error'>
						You have validation errors, resolve all problems before clicking SAVE.
					</Alert>
				)}
			</div>
			<div className='editor-modal-body'>{children}</div>
			<div className='editor-modal-footer'>
				<InverseButton
					fullWidth={false}
					onClick={protectClose}
					name={hasUnsavedChanges && displayAlert ? 'DISCARD' : 'CLOSE'}
					disabled={clicked || loading}
					theme={theme}
					data-cy='close-button'
				/>
				{handleSave && !readOnly ? (
					<Button onClick={onSaveClick} disabled={clicked || loading || saveDisabled} data-cy='save-button'>
						Save
					</Button>
				) : null}
			</div>
		</div>
	);
};
